import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAllPredefinedMiscItems,
  createPredefinedMiscItem,
  updatePredefinedMiscItem,
  deletePredefinedMiscItem,
} from "../../api/Api";
import { AuthContext } from "../../utils/AuthContext";
import { useContext } from "react";

const PredefinedMiscItems = ({ onUpdate }) => {
  const { selectedBranchId } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await getAllPredefinedMiscItems();
      if (response && response.data) {
        setItems(response.data);
      }
    } catch (err) {
      toast.error("Failed to load items.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !amount) {
      toast.error("Please enter a name and an amount.");
      return;
    }

    try {
      const branchId = selectedBranchId || localStorage.getItem("selectedBranchId");
      if (!branchId) {
        toast.error("Branch ID is missing. Please re-select your branch.");
        return;
      }
      const payload = { name, amount: Number(amount), branch: branchId };
      if (editingItem) {
        await updatePredefinedMiscItem(editingItem._id, payload);
        toast.success("Item updated successfully!");
      } else {
        await createPredefinedMiscItem(payload);
        toast.success("Item created successfully!");
      }
      setName("");
      setAmount("");
      setEditingItem(null);
      fetchItems();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error("Error saving predefined item:", err);
      toast.error(err.message || "Failed to save item.");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setName(item.name);
    setAmount(item.amount);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deletePredefinedMiscItem(id);
        toast.success("Item deleted successfully!");
        fetchItems();
        if (onUpdate) onUpdate();
      } catch (err) {
        console.error("Error deleting predefined item:", err);
        toast.error(err.message || "Failed to delete item.");
      }
    }
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setName("");
    setAmount("");
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto bg-white rounded-xl shadow-md min-h-screen">


      {/* Header */}
      <h2 className="text-2xl font-bold mb-6 text-[#16bab1]">
        Predefined Miscellaneous Items
      </h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mb-8 p-6 border border-gray-100 rounded-xl bg-gray-50 shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-gray-700">
          {editingItem ? "Edit Item" : "Add New Item"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-600 mb-1">Item Name</label>
            <input
              type="text"
              placeholder="Enter item name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#16bab1] bg-white"
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-600 mb-1">Amount</label>
            <input
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#16bab1] bg-white"
            />
          </div>
          <div className="flex gap-2 md:col-span-1">
            <button
              type="submit"
              className="flex-grow bg-[#16bab1] text-white px-4 py-3 rounded-lg hover:bg-[#139a93] transition font-medium shadow-sm"
            >
              {editingItem ? "Update" : "Add"}
            </button>
            {editingItem && (
              <button
                type="button"
                onClick={cancelEdit}
                className="bg-gray-200 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Items List */}
      <h3 className="text-xl font-bold mb-4 text-gray-800">Existing Items</h3>
      {loading ? (
        <p className="text-center py-4 text-gray-500">Loading items...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full bg-white">
            <thead className="bg-[#16bab1] text-white">
              <tr>
                <th className="py-3 px-4 text-left font-semibold text-sm">Name</th>
                <th className="py-3 px-4 text-left font-semibold text-sm w-32">Amount</th>
                <th className="py-3 px-4 text-center font-semibold text-sm w-48">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.length > 0 ? (
                items.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50 transition">
                    <td className="py-3 px-4 text-gray-800 font-medium text-sm">{item.name}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">₹{item.amount}</td>
                    <td className="py-3 px-4 flex justify-center gap-3">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-sm transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="text-red-600 hover:text-red-800 font-medium text-sm transition"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-gray-500 bg-gray-50">
                    No predefined items found. Start by adding one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PredefinedMiscItems;